// SPDX-License-Identifier: MIT
// Desktop attachment bridge — durable, backed by a local attachments.json via
// the Rust get_attachments/set_attachments commands. No Supabase, no binary kept
// on disk : what matters on desktop is a file's EXTRACTED TEXT, which feeds the
// discover/enriched context. We persist {metadata + text} so the file list AND
// that context survive an app restart, and re-hydrate the in-memory context on
// list() (the mount-time load). Multimodal binaries are a follow-up (ai-lab).
import type { AttachmentBridge } from './attachment-types';
import { extractAttachmentText, addToContext, removeFromContext, type AttachmentRow } from '../projects/attachments';
import { tauriInvoke } from './tauri';

// Persisted shape: metadata + the extracted text (null for binaries).
interface StoredAttachment extends AttachmentRow {
  text: string | null;
}
type AttachmentsFile = Record<string, StoredAttachment[]>;

const fileKey = (userId: string, projectId: string): string => `${userId}/${projectId}`;

async function readAll(): Promise<AttachmentsFile> {
  const raw = await tauriInvoke()('get_attachments');
  if (typeof raw !== 'string' || raw.length === 0) return {};
  try {
    const obj = JSON.parse(raw) as AttachmentsFile;
    return obj && typeof obj === 'object' ? obj : {};
  } catch {
    return {};
  }
}

async function writeAll(all: AttachmentsFile): Promise<void> {
  await tauriInvoke()('set_attachments', { json: JSON.stringify(all) });
}

function toRow(s: StoredAttachment): AttachmentRow {
  return { path: s.path, name: s.name, size: s.size, mime: s.mime, created_at: s.created_at };
}

export const desktopAttachmentBridge: AttachmentBridge = {
  async upload(userId, projectId, file) {
    const text = await extractAttachmentText(file);
    const stored: StoredAttachment = {
      path: `local/${userId}/${projectId}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}-${file.name}`,
      name: file.name,
      size: file.size,
      mime: file.type || 'application/octet-stream',
      created_at: new Date().toISOString(),
      text,
    };
    const all = await readAll();
    const k = fileKey(userId, projectId);
    all[k] = [stored, ...(all[k] ?? [])];
    await writeAll(all);
    // Make the text available to the current session immediately (the context
    // getters are sync ; list() handles the post-restart re-hydration).
    if (text !== null) addToContext(userId, projectId, file.name, text);
    return toRow(stored);
  },
  async list(userId, projectId) {
    const stored = (await readAll())[fileKey(userId, projectId)] ?? [];
    // Re-hydrate the in-memory context from persisted text so discover/enriched
    // have their inputs back after an app restart.
    for (const s of stored) if (s.text !== null) addToContext(userId, projectId, s.name, s.text);
    return stored.map(toRow);
  },
  async remove(path) {
    const all = await readAll();
    for (const [k, rows] of Object.entries(all)) {
      const gone = rows.find((r) => r.path === path);
      if (!gone) continue;
      all[k] = rows.filter((r) => r.path !== path);
      const [userId, projectId] = k.split('/');
      removeFromContext(userId, projectId, gone.name);
    }
    await writeAll(all);
  },
  async signedUrl() {
    return null;
  },
  supportsOpen: false,
};
